/**
 * Shared design tokens — single source of truth for RN (Unistyles) and Web (CSS).
 *
 * Values mirror `apps/WunderGuide/App.tsx` darkTheme + RN component constants.
 * The web side reads these into CSS vars (see apps/wunderguide-web/src/index.css)
 * and the RN side wires them into Unistyles in App.tsx.
 */

export const colors = {
  // Brand
  primary: '#aa12ff',
  primaryHover: '#b833ff',
  primaryActive: '#9000e0',
  secondary: '#ff7eb9',
  accent: '#1ff4ff',

  // Surfaces
  bg: '#000',
  bgGradientStart: '#2c003e',
  bgGradientEnd: '#d90091',
  surface: 'rgba(255, 255, 255, 0.05)',
  surfaceElevated: 'rgba(255, 255, 255, 0.08)',
  surfaceStrong: 'rgba(0, 0, 0, 0.45)',

  // Text
  text: 'rgba(255, 255, 255, 0.92)',
  textMuted: 'rgba(255, 255, 255, 0.65)',
  textSubtle: 'rgba(255, 255, 255, 0.45)',
  textInverted: '#000',

  // Borders
  border: 'rgba(255, 255, 255, 0.12)',
  borderStrong: 'rgba(255, 255, 255, 0.25)',

  // Status
  success: '#2ecc71',
  warning: '#f39c12',
  error: '#ff5470',
  info: '#1ff4ff',
} as const;

/** Spacing scale — matches RN `gap(v) = v * 8`. */
export const gap = (v: number) => v * 8;

export const radii = {
  sm: 6,
  md: 12,
  lg: 20,
  pill: 999,
} as const;

export const shadows = {
  sm: '0 2px 8px rgba(0, 0, 0, 0.3)',
  md: '0 8px 24px rgba(0, 0, 0, 0.4)',
  glow: '0 0 24px rgba(170, 18, 255, 0.45)',
} as const;

export const typography = {
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  // Variant sizes mirror common/Text.tsx (RN)
  variants: {
    h1: { fontWeight: 900, fontSize: 32, marginBottom: 12 },
    h2: { fontWeight: 700, fontSize: 24, marginBottom: 10 },
    h3: { fontWeight: 600, fontSize: 20, marginBottom: 8 },
    body: { fontWeight: 400, fontSize: 16 },
    label: {
      fontWeight: 500,
      fontSize: 13,
      letterSpacing: 1,
      textTransform: 'uppercase' as const,
      marginBottom: 6,
    },
    pill: { fontWeight: 500, fontSize: 12 },
  },
} as const;

export const breakpoints = {
  xs: 0,
  sm: 300,
  md: 500,
  lg: 800,
  xl: 1200,
} as const;
